import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const CompanyOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [isLoading, setIsLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid session');
      navigate('/');
      return;
    }

    // Fetch company data after successful payment
    const fetchCompanyData = async () => {
      try {
        setIsLoading(true);
        // TODO: Call backend API to verify session and get company data
        // For now, just simulate success
        setTimeout(() => {
          setCompanyData({
            name: 'Your Company',
            plan: 'Team Plan',
            seats: 3
          });
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching company data:', error);
        toast.error('Failed to load company information');
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, [sessionId, navigate]);

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSendingInvite(true);
    
    try {
      // TODO: Call backend API to send invitation
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Setting up your team workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Banner */}
        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Welcome to Explanatum! 🎉
          </h1>
          <p className="text-lg text-gray-600">
            Your team workspace is ready. You're now on a 7-day free trial.
          </p>
        </div>

        {/* Company Details */}
        {companyData && (
          <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Team Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Company</div>
                <div className="text-xl font-semibold text-indigo-600">{companyData.name}</div>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Plan</div>
                <div className="text-xl font-semibold text-indigo-600">{companyData.plan}</div>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Team Seats</div>
                <div className="text-xl font-semibold text-indigo-600">{companyData.seats}</div>
              </div>
            </div>
          </div>
        )}

        {/* Invite Team Members */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invite Your Team</h2>
          <p className="text-gray-600 mb-6">Add team members to collaborate on your business projects.</p>
          
          <form onSubmit={handleSendInvite} className="space-y-4">
            <div>
              <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="inviteEmail"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="colleague@company.com"
                disabled={isSendingInvite}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setInviteRole('member')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    inviteRole === 'member'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isSendingInvite}
                >
                  Member
                </button>
                <button
                  type="button"
                  onClick={() => setInviteRole('admin')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    inviteRole === 'admin'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isSendingInvite}
                >
                  Admin
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={isSendingInvite}
              className={`w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors ${
                isSendingInvite ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSendingInvite ? 'Sending Invitation...' : 'Send Invitation'}
            </button>
          </form>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Next Steps</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <span className="font-semibold">Explore the Dashboard</span>
                <p className="text-gray-600 text-sm">Access 45+ tools for marketing, finance, and legal/HR</p>
              </div>
            </li>
            <li className="flex items-start">
              <svg className="w-6 h-6 text-indigo-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <span className="font-semibold">Invite Team Members</span>
                <p className="text-gray-600 text-sm">Collaborate with your team on business projects</p>
              </div>
            </li>
            <li className="flex items-start">
              <svg className="w-6 h-6 text-purple-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <div>
                <span className="font-semibold">Start Creating</span>
                <p className="text-gray-600 text-sm">Generate business plans, logos, content, and more</p>
              </div>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex-1 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform transition-all hover:-translate-y-1"
          >
            Go to Dashboard
          </button>
          <button
            onClick={handleSkip}
            className="flex-1 py-4 bg-white border-2 border-gray-300 text-gray-700 text-lg font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            Skip for Now
          </button>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Need help? Check out our <a href="/help" className="text-indigo-600 hover:underline">Help Center</a> or <a href="/contact" className="text-indigo-600 hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
};

export default CompanyOnboarding;
